import { useState } from "react";
import { Link } from "react-router-dom";
import { submitLeadToZoho } from "@/lib/zoho";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";

// Stored alongside the lead so we have a record of exactly what was agreed to.
const CONSENT_TEXT =
  "I agree that Rao Investment Solutions may contact me by phone, email or WhatsApp about this enquiry. I can withdraw consent at any time. My details are handled per the";

// Reads UTM params + current page and posts the lead straight to Zoho CRM via
// lib/zoho.js. The markup here is ours; only the destination is Zoho's.
export default function LeadForm({
  audience,
  calculator,
  calculatorResult,
  compact = false,
  dark = false,
  title = "Request a callback",
  subtitle = "Share your details and Karthik's team will reach out within one business day.",
}) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    if (!consent) {
      toast.error("Please agree to be contacted before submitting.");
      return;
    }
    setLoading(true);
    const params = new URLSearchParams(window.location.search);
    try {
      await submitLeadToZoho({
        ...form,
        audience,
        calculator,
        calculator_result: calculatorResult,
        source_page: window.location.pathname,
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
        consent: true,
        consent_text: CONSENT_TEXT,
      });
      setDone(true);
      toast.success("Thank you. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call or email us instead.");
    } finally {
      setLoading(false);
    }
  };

  const labelCls = `font-sans text-xs uppercase tracking-[0.18em] ${dark ? "text-white/50" : "text-slate"} mb-2 block`;
  const inputCls = `w-full px-4 py-3 font-sans text-sm bg-transparent border ${
    dark ? "border-white/20 text-white placeholder:text-white/30" : "border-navy/15 text-offblack placeholder:text-slate/50"
  } focus:border-orange focus:outline-none transition-colors`;

  if (done) {
    return (
      <div className={`p-8 ${dark ? "text-white" : "text-offblack"} flex flex-col items-start gap-4`} data-testid="lead-form-success">
        <CheckCircle2 className="w-10 h-10 text-orange" strokeWidth={1.5} />
        <h3 className="font-serif text-2xl">Request received</h3>
        <p className={`font-sans text-sm ${dark ? "text-white/60" : "text-slate"}`}>
          Thank you, {form.name.split(" ")[0]}. A member of the team will contact you within one business day.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={compact ? "" : ""} data-testid="lead-form">
      {title && <h3 className={`font-serif text-2xl md:text-3xl ${dark ? "text-white" : "text-navy"}`}>{title}</h3>}
      {subtitle && <p className={`mt-2 mb-6 font-sans text-sm ${dark ? "text-white/60" : "text-slate"}`}>{subtitle}</p>}

      <div className={`grid ${compact ? "grid-cols-1" : "sm:grid-cols-2"} gap-4`}>
        <div>
          <label className={labelCls}>Name</label>
          <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="lead-name" />
        </div>
        <div>
          <label className={labelCls}>Phone</label>
          <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91" data-testid="lead-phone" />
        </div>
        <div className={compact ? "" : "sm:col-span-2"}>
          <label className={labelCls}>Email</label>
          <input className={inputCls} type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="lead-email" />
        </div>
        <div className={compact ? "" : "sm:col-span-2"}>
          <label className={labelCls}>What are you interested in?</label>
          <input className={inputCls} value={form.interest} onChange={set("interest")} placeholder="e.g. SIP, retirement planning, corporate bonds" data-testid="lead-interest" />
        </div>
        {!compact && (
          <div className="sm:col-span-2">
            <label className={labelCls}>Message (optional)</label>
            <textarea className={inputCls} rows={3} value={form.message} onChange={set("message")} placeholder="Tell us a little about your goals" data-testid="lead-message" />
          </div>
        )}
      </div>

      <label className={`mt-6 flex items-start gap-3 cursor-pointer font-sans text-xs leading-relaxed ${dark ? "text-white/60" : "text-slate"}`}>
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 w-4 h-4 shrink-0 accent-orange cursor-pointer"
          data-testid="lead-consent"
        />
        <span>
          {CONSENT_TEXT}{" "}
          <Link
            to="/privacy"
            className={`underline underline-offset-2 ${dark ? "hover:text-white" : "hover:text-orange"}`}
          >
            Privacy Policy
          </Link>
          .
        </span>
      </label>
      <button
        type="submit"
        disabled={loading}
        className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-orange text-white font-sans text-sm font-medium hover:bg-orange-dark transition-colors disabled:opacity-60 group"
        data-testid="lead-submit"
      >
        {loading ? "Sending..." : "Submit request"}
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
      </button>
    </form>
  );
}
